import type { ReactNode } from 'react';
import React, { Fragment } from 'react';
import { cssMap } from '@compiled/react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';
import { MiniRenderer } from '@confluence/mini-renderer';

import { scrollToHeader } from './scrollToHeader.tsx';

export type TocHeading = {
	content: (ADFEntity | undefined)[];
	level: number;
	id: string;
	outline: string[];
};

export type TocRenderer<A> = {
	renderItem: (heading: TocHeading, depth: number, renderedChildren: A) => A;
	mergeItems: (items: A[]) => A;
};

export const EmptyTocItem = 'toc-empty-item';

const styles = cssMap({
	link: { font: token('font.body.large') },
});

function normalizeHeadings(elements: ADFEntity[]): ADFEntity[] {
	const minLevel = elements.reduce((min, el) => Math.min(min, el?.attrs?.level ?? 6), Infinity);

	if (elements[0]?.attrs?.level === minLevel) {
		return elements;
	}

	return [
		{
			type: 'toc',
			attrs: { level: minLevel },
			content: [{ text: EmptyTocItem, type: 'string' }],
		} as ADFEntity,
		...elements,
	];
}

export const renderTocDirect = <A,>(headerElements: ADFEntity[], renderer: TocRenderer<A>): A => {
	if (headerElements.length === 0) {
		return renderer.mergeItems([]);
	}

	const headings = normalizeHeadings(headerElements);

	const stack: Array<{ level: number; heading: TocHeading; childResults: A[] }> = [];
	const rootResults: A[] = [];
	const counters: number[] = [];

	function close() {
		const frame = stack.pop()!;
		const children = renderer.mergeItems(frame.childResults);
		const result = renderer.renderItem(frame.heading, stack.length, children);

		// Use index access instead of `Array.prototype.at(-1)` because the latter
		// is unsupported in Safari < 15.4 (lint: no-restricted-syntax).
		const parent = stack.length > 0 ? stack[stack.length - 1] : undefined;
		if (parent) {
			parent.childResults.push(result);
		} else {
			rootResults.push(result);
		}
	}

	for (const entity of headings) {
		const level = entity.attrs?.level ?? 1;

		while (stack.length > 0 && stack[stack.length - 1].level >= level) {
			close();
		}

		if (counters.length <= stack.length) {
			counters.push(0);
		}
		counters[stack.length]++;
		counters.length = stack.length + 1;
		const outline = counters.map(String);

		const isEmpty = entity.content?.[0]?.text === EmptyTocItem;
		const heading: TocHeading = {
			content: entity.content ?? [],
			level,
			id: isEmpty ? EmptyTocItem : (entity.attrs?.id ?? ''),
			outline,
		};

		stack.push({ level, heading, childResults: [] });
	}

	while (stack.length > 0) {
		close();
	}

	return renderer.mergeItems(rootResults);
};

function renderContent(
	content: (ADFEntity | undefined)[],
	cloudId: string,
	userId: string,
): ReactNode {
	const nodes = content.filter(Boolean) as ADFEntity[];
	if (nodes.length === 0) {
		return null;
	}
	// Only collapse to a string when the text nodes have NO marks. This preserves
	// formatting marks (bold, italic, code, link, etc.) inside headings.
	const isFastPathEligible = expVal('confluence_toc_render_perf', 'isEnabled', false)
		? nodes.every(
				(n) => n.type === 'hardBreak' || (n.type === 'text' && (!n.marks || n.marks.length === 0)),
			)
		: nodes.every((n) => n.type === 'text' || n.type === 'hardBreak');
	if (isFastPathEligible) {
		return nodes.map((n) => (n.type === 'hardBreak' ? '' : (n.text ?? ''))).join('');
	}
	return <MiniRenderer adfEntities={content} cloudId={cloudId} userId={userId} />;
}

function renderLink(
	heading: TocHeading,
	outline: string,
	cloudId: string,
	userId: string,
	shouldExpandParentExpandsOnScroll: boolean,
): ReactNode {
	const outlineStr = heading.outline.join('.');
	const href = `#${encodeURIComponent(heading.id)}`;
	return (
		<Anchor
			testId="toc-item-body"
			data-outline={outlineStr}
			href={href}
			onClick={() => scrollToHeader(href, shouldExpandParentExpandsOnScroll)}
			css={styles.link}
		>
			{outline === 'true' && <span>{outlineStr} </span>}
			{renderContent(heading.content, cloudId, userId)}
		</Anchor>
	);
}

function renderFlatToc(
	headerelements: ADFEntity[],
	outline: string,
	separators: string[],
	cloudId: string,
	userId: string,
	shouldExpandParentExpandsOnScroll: boolean,
): ReactNode {
	const flatItems = renderTocDirect<ReactNode[]>(headerelements, {
		renderItem: (heading, _depth, children) => {
			if (heading.id === EmptyTocItem) {
				return children ?? [];
			}
			return [
				renderLink(heading, outline, cloudId, userId, shouldExpandParentExpandsOnScroll),
				...(children ?? []),
			];
		},
		mergeItems: (merged) => merged.flat(),
	});
	if (flatItems.length === 0) {
		return null;
	}
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
		<span data-testid="flat-style-toc-item-container" className="toc-item-container">
			{flatItems.map((item, idx) => (
				<Fragment key={idx}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<span className="toc-separator">{idx === 0 ? separators[0] : separators[1]}</span>
					{item}
				</Fragment>
			))}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<span className="toc-separator">{separators[2]}</span>
		</span>
	);
}

function renderListToc(
	headerelements: ADFEntity[],
	outline: string,
	cssliststyle: string,
	csslistindent: string,
	cloudId: string,
	userId: string,
	shouldExpandParentExpandsOnScroll: boolean,
): ReactNode {
	return renderTocDirect<ReactNode>(headerelements, {
		renderItem: (heading, _depth, children) => {
			const isEmpty = heading.id === EmptyTocItem;
			return (
				<li
					key={heading.outline.join('.')}
					data-testid="list-style-toc-item-container"
					style={{ listStyle: isEmpty ? 'none' : (cssliststyle ?? 'default') }}
				>
					{!isEmpty &&
						renderLink(heading, outline, cloudId, userId, shouldExpandParentExpandsOnScroll)}
					{children}
				</li>
			);
		},
		mergeItems: (listItems) => {
			if (listItems.length === 0) {
				return null;
			}
			return (
				<ul data-testid="list-style-toc-level-container" style={{ paddingLeft: csslistindent }}>
					{listItems}
				</ul>
			);
		},
	});
}

export const renderReducedToc = (
	tocOptions: {
		headerelements: ADFEntity[];
		structure: string;
		outline: string;
		cssliststyle: string;
		csslistindent: string;
	},
	separators: string[],
	cloudId: string,
	userId: string,
	shouldExpandParentExpandsOnScroll = false,
): ReactNode => {
	const { headerelements, structure, outline, cssliststyle, csslistindent } = tocOptions;

	if (!headerelements?.length) {
		return null;
	}

	if (structure === 'flat') {
		return renderFlatToc(
			headerelements,
			outline,
			separators,
			cloudId,
			userId,
			shouldExpandParentExpandsOnScroll,
		);
	}

	return renderListToc(
		headerelements,
		outline,
		cssliststyle,
		csslistindent,
		cloudId,
		userId,
		shouldExpandParentExpandsOnScroll,
	);
};
